import {UploadOutlined} from '@ant-design/icons'
import {Alert, Button, Card, Col, List, notification, Row, Space, Table, Typography, Upload} from 'antd'
import React, {useState} from 'react'
import {useNavigate} from 'react-router-dom'
import * as XLSX from 'xlsx'

import {useSinifTopluEkleMutation} from "../../../../../gql/GeneratedGraphqlTypes";

const EXPECTED_COLUMNS = ['id', 'isim', 'kampus', 'kategori_id', 'mevcut', 'aktif']

const {Title, Text} = Typography
const ClassImportPage = () => {
    const [file, setFile] = useState<File | null>(null)
    const [tableData, setTableData] = useState<any[]>([])
    const [columns, setColumns] = useState<any[]>([])
    const navigate = useNavigate()
    const [sinifTopluEkle] = useSinifTopluEkleMutation()
    const [errorMessages, setErrorMessages] = useState<Record<string, string>>({})
    const handleFileChange = (info: any) => {
        const file = info.fileList.length > 0 ? info.fileList[0].originFileObj : null

        if (file) {
            setFile(file)
            readExcel(file)
        } else {
            setFile(null)
            setTableData([])
            setColumns([])
            setErrorMessages({})
        }
    }

    const handleRemove = () => {
        setFile(null)
        setTableData([])
        setColumns([])
        setErrorMessages({})
    }

    const readExcel = (file: File) => {
        const reader = new FileReader();
        reader.readAsBinaryString(file);
        reader.onload = (event) => {
            try {
                const data = event.target?.result;
                const workbook = XLSX.read(data, {type: "binary"});
                const sheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[sheetName];

                const jsonData: any[][] = XLSX.utils.sheet_to_json(sheet, {header: 1});

                if (!jsonData.length) {
                    notification.error({
                        message: "Dosya boş veya okunamıyor.",
                    });
                    return;
                }

                const [headersRaw, ...rows] = jsonData;
                const headers = headersRaw.map((h) => String(h).trim());


                if (!headers.length || rows.length === 0) {
                    notification.error({ message: "Geçersiz dosya formatı" });
                    return;
                }

                const validHeaders = headers.filter((header) => EXPECTED_COLUMNS.includes(header));
                const nonEmptyRows = rows.filter((row) =>
                    row.some(cell => cell !== null && cell !== undefined && String(cell).trim() !== "")
                )
                // **Hataları kontrol etmek için yeni bir obje**
                const newErrorMessages: Record<number, string> = {};

                // **Geçerli satırları tutacak liste**
                const validRows: any[] = [];

                nonEmptyRows.forEach((row, rowIndex) => {
                    const formattedRow: Record<string, any> = {};
                    validHeaders.forEach((header, colIndex) => {
                        const originalIndex = headers.indexOf(header);
                        formattedRow[header] = row[originalIndex];
                    });

                    const errors = [];
                    const hasId = formattedRow["id"] !== undefined && formattedRow["id"] !== null && String(formattedRow["id"]).trim() !== "";

                    if (!hasId) {
                        if (!formattedRow["kampus"] || isNaN(Number(formattedRow["kampus"]))) {
                            errors.push("Şube ID eksik veya geçersiz");
                        }
                        if (!formattedRow["kategori_id"] || isNaN(Number(formattedRow["kategori_id"]))) {
                            errors.push("Sınıf Kategorisi IDD eksik veya geçersiz");
                        }
                    }

                    if (errors.length > 0) {
                        newErrorMessages[rowIndex] = `Satır ${rowIndex + 2}: ${errors.join(", ")}`;
                    } else {
                        validRows.push(formattedRow);
                    }
                });

                // Eğer hata varsa dosyayı yükleme
                if (Object.keys(newErrorMessages).length > 0) {
                    setErrorMessages(newErrorMessages);
                    setTableData([]);
                    setColumns([]);
                    notification.error({
                        message: "Hatalı satırlar bulundu. Lütfen düzeltin.",
                    });
                    return;
                }
                setErrorMessages({});

                // **Kolonları oluştur**
                const tableColumns = validHeaders.map((header, index) => ({
                    title: header,
                    dataIndex: `col${index}`,
                    key: `col${index}`,
                }));

                const tableRows = validRows.map((row, rowIndex) => {
                    const rowData: Record<string, any> = { key: rowIndex };
                    validHeaders.forEach((header, colIndex) => {
                        rowData[`col${colIndex}`] = row[header];
                    });
                    return rowData;
                });

                setColumns(tableColumns);
                setTableData(tableRows);
            } catch (error) {
                notification.error({
                    message: "Dosya okunurken hata oluştu.",
                });
            }
        };
    };

    const handleSubmit = async () => {
        if (!file) {
            notification.error({
                message: "Lütfen bir dosya seçin!",
            });
            return
        }

        if (tableData.length === 0) {
            notification.error({
                message: "Tabloda veri bulunmuyor!",
            });
            return
        }

        // JSON formatında liste oluştur
        const jsonData = tableData.map((row) => {
            const formattedRow: Record<string, any> = {};
            columns.forEach((col, index) => {
                const title = col.title;
                if (EXPECTED_COLUMNS.includes(title)) {
                    formattedRow[title] = row[`col${index}`];
                }
            });

            // **Beklenen format ile uyuştuğundan emin ol**
            return {
                id: formattedRow["id"] ? Number(formattedRow["id"]) : undefined,  // ID varsa güncelleme
                isim: String(formattedRow["isim"]).trim(),
                sube_id: Number(formattedRow["kampus"]),
                sinif_kategorisi_id: Number(formattedRow["kategori_id"]),
                mevcut: Number(formattedRow["mevcut"]),
                aktif: Boolean(formattedRow["aktif"])
            };
        });

        try {
            // **GraphQL mutasyonunu çağır**
            const {data, errors} = await sinifTopluEkle({variables: {input: jsonData}});
            if (errors) {
                notification.error({
                    message: "Kayıt Hatası",
                    description: data?.sinifTopluEkle.message || "Verileri kaydederken bir hata oluştu. Lütfen tekrar deneyin."
                });
                return;
            }

            if (data?.sinifTopluEkle?.success) {
                notification.success({
                    message: '',
                    description: data?.sinifTopluEkle.message,
                    duration: 6,
                });
                navigate(-1); // İşlem başarılıysa önceki sayfaya yönlendir
            } else {
                notification.error({
                    message: "Kayıt Başarısız",
                    description: data?.sinifTopluEkle?.message || "Bilinmeyen bir hata oluştu."
                });
            }
        } catch (error) {
            notification.error({
                message: "Bağlantı Hatası",
                description: "Sunucuya bağlanırken bir hata oluştu. Lütfen tekrar deneyin."
            });
        }
    }
    return (
        <div className="okul-siparis-import-page">
            <Card title="Sınıflar İçe Aktar" bordered={false}>
                <Space direction="vertical" style={{width: '100%'}} size="middle">
                    <Text>
                        Bu içe aktarıcı ilgili alanları içe aktaracaktır: <Text
                        code>{EXPECTED_COLUMNS.join(', ')}</Text>
                    </Text>
                    <Row>
                        <Col span={4}>
                            <Upload
                                beforeUpload={() => false}
                                onChange={handleFileChange}
                                fileList={file ? [{uid: '-1', name: file.name, status: 'done'}] : []}
                                onRemove={handleRemove}
                                showUploadList={{showRemoveIcon: true}}
                            >
                                <Button icon={<UploadOutlined/>}>Dosya Seç</Button>
                            </Upload>
                        </Col>
                    </Row>

                    {tableData.length > 0 && (
                        <>
                            <Title level={5}>Önizleme:</Title>
                            <Table columns={columns} dataSource={tableData} pagination={{pageSize: 5}}/>
                        </>
                    )}
                    {/* Eğer hata varsa liste olarak göster */}
                    {Object.keys(errorMessages).length > 0 && (
                        <List
                            header={<Title level={5}>Hatalar</Title>}
                            bordered
                            dataSource={Object.values(errorMessages)}
                            renderItem={(item) => <List.Item><Alert message={item} type="error" showIcon/></List.Item>}
                        />
                    )}
                    <div className="okul-yonetim-footer-buttons">
                        <Button className="back-btn" onClick={() => navigate(-1)}>
                            Geri
                        </Button>
                        <Button className="update-btn" type="primary" onClick={handleSubmit}
                                disabled={!file || Object.keys(errorMessages).length > 0}>
                            Kaydet
                        </Button>
                    </div>
                </Space>
            </Card>
        </div>
    )
}

export default ClassImportPage
